import { Injectable } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, Observable, combineLatest, debounceTime, first, of } from "rxjs";
import { EditionFeatureService } from "@webapp/accounts/services/edition-feature.service";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import {
  GlobalSearchResult,
  SearchFacetsAccessMap,
  SearchFacetsOptions,
  SearchFacetsOptionsEnum,
  SearchFacetsOptionsMap,
  SearchIndicators,
} from "@webapp/search/models/search.models";
import { SearchFacade } from "@webapp/search/services/search-facade.service";
import { convertToOrderedArray, getFacetsAndQueryParams, getSearchBodyV2 } from "@webapp/search/utils/global-search.utils";

@UntilDestroy()
@Injectable()
export class GlobalSearchMediatorService {
  private searchResults = new BehaviorSubject<GlobalSearchResult[]>(null);
  private currentFacet = new BehaviorSubject<SearchFacetsOptions>(SearchFacetsOptionsEnum.Everywhere);
  private facetToggleOptions = new BehaviorSubject<SearchFacetsOptions[]>(null);
  private searchTerm = new BehaviorSubject<string>(null);
  private indicators = new BehaviorSubject<SearchIndicators>({});

  private readonly facetOptionsMap: SearchFacetsOptionsMap = {} as SearchFacetsOptionsMap;

  constructor(
    private searchFacade: SearchFacade,
    private permissionsFacade: PermissionsFacade,
    private featureToggleFacade: FeatureTogglesFacade,
    private editionFacade: EditionFeatureService
  ) {
    this.setFacetsOptions();

    this.searchTerm$.pipe(untilDestroyed(this), debounceTime(500)).subscribe(() => this.loadSearchData());
    this.currentFacet$.pipe(untilDestroyed(this)).subscribe(() => this.loadSearchData());
  }

  public get currentFacet$(): Observable<SearchFacetsOptions> {
    return this.currentFacet.asObservable();
  }

  public get searchResults$(): Observable<GlobalSearchResult[]> {
    return this.searchResults.asObservable();
  }

  public get facetToggleOptions$(): Observable<SearchFacetsOptions[]> {
    return this.facetToggleOptions.asObservable();
  }

  public get searchTerm$(): Observable<string> {
    return this.searchTerm.asObservable();
  }

  public get indicators$(): Observable<SearchIndicators> {
    return this.indicators.asObservable();
  }

  public setCurrentFacet(currentFacet: SearchFacetsOptions): void {
    this.currentFacet.next(currentFacet);
  }

  public setSearchTerm(searchTerm: string): void {
    this.searchTerm.next(searchTerm);
  }

  private loadSearchData(): void {
    const { facets, queryParams } = getFacetsAndQueryParams(this.currentFacet.getValue(), this.facetOptionsMap);

    if (!this.searchTerm.getValue() || !facets.length) {
      this.searchResults.next(null);
      this.indicators.next({ gettingSearchData: null });
      return;
    }
    this.indicators.next({ gettingSearchData: { progress: true } });

    this.searchFacade
      .searchDataV2$(getSearchBodyV2(this.searchTerm.getValue(), facets), queryParams)
      .pipe(first())
      .subscribe((searchData) => {
        this.searchResults.next(convertToOrderedArray(searchData));
        this.indicators.next({ gettingSearchData: null });
      });
  }

  private setFacetsOptions(): void {
    const optionsArr: SearchFacetsOptions[] = [];
    Object.values(SearchFacetsOptionsEnum).forEach((val) => {
      const accessMap = SearchFacetsAccessMap[val];
      combineLatest({
        hasPermissions: accessMap.permissions ? this.permissionsFacade.hasMultiplePermissions$(accessMap.permissions) : of(true),
        isFeatureEnabled: accessMap.featureFlag ? this.featureToggleFacade.isFeatureAvailable$(accessMap.featureFlag) : of(true),
        isEditionEnabled: accessMap.editionFeature ? this.editionFacade.hasFeature$(accessMap.editionFeature) : of(true),
      })
        .pipe(first(), untilDestroyed(this))
        .subscribe(({ hasPermissions, isFeatureEnabled, isEditionEnabled }) => {
          if (hasPermissions && isFeatureEnabled && isEditionEnabled) {
            this.facetOptionsMap[val] = val.toLowerCase() as SearchFacetsOptionsEnum;
            optionsArr.push(val);
            this.facetToggleOptions.next(optionsArr);
          }
        });
    });
  }
}
